import { setAddressItem } from '@strix/checkout/helpers/updateCartAddress'
import { types } from './types'
import camelCaseKeysToUnderscore from '~/utils/stringTransform/camelCaseKeysToUnderscore'

export const actions = {
  async refreshCart ({ commit, dispatch, getters, state }, cartId = this.$cart.cartId()) {
    commit(types.SET_CART_LOADED, false)

    if (!cartId) {
      await dispatch('getCartIdFromCookie')
      cartId = state.id
    }

    if (!cartId) {
      await dispatch('createEmptyCart')
      cartId = state.id
    }

    await this.$graphql({
      requestPayload: {
        fetchPolicy: 'network-only',
        query: getters.getCurrentStep === 0
          ? (await import('../../graphql/queries/cartMini.graphql')).default
          : (await import('../../graphql/queries/cart.graphql')).default,
        variables: {
          cartId,
        },
      },
      successHandler: async ({ data: { cart } }) => {
        await dispatch('updateCart', cart)
        await dispatch('isCartReadOnly', cart.isReadonly)

        if (cart.isReadonly) {
          await dispatch('setShippingMethodReadOnly', cart.shippingAddresses?.[0]?.selectedShippingMethod?.methodTitle || '')
          await dispatch('setBillingAddress', cart.billingAddress)
        }
      },
      errorHandler: async () => {
        await dispatch('createEmptyCart')
      },
    })

    commit(types.SET_CART_LOADED, true)
  },

  getCartIdFromCookie ({ commit }) {
    commit(types.SET_CART_ID_FROM_COOKIE)
  },

  async addCouponCode ({ dispatch }, coupon) {
    const cartId = this.$cart.cartId()

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/applyCouponToCart.graphql')).default,
        variables: {
          input: {
            cart_id: cartId,
            coupon_code: coupon,
          },
        },
      },
      successHandler: async ({ data: { applyCouponToCart } }) => {
        await dispatch('updateCart', applyCouponToCart.cart)
      },
    })
  },
  async removeCouponCode ({ dispatch }) {
    const cartId = this.$cart.cartId()

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/removeCouponFromCart.graphql')).default,
        variables: {
          input: {
            cart_id: cartId,
          },
        },
      },
      successHandler: async ({ data: { removeCouponFromCart } }) => {
        await dispatch('updateCart', removeCouponFromCart.cart)
      },
    })
  },
  async createEmptyCart ({ commit, dispatch }) {
    await dispatch('resetCart')

    commit(types.SET_CART_LOADED, false)

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/createEmptyCart.graphql')).default,
      },
      successHandler: ({ data: { createEmptyCart } }) => {
        commit(types.SET_CART_ID, createEmptyCart)
      },
    })

    commit(types.SET_CART_LOADED, true)
  },
  async getCustomerCart ({ commit, dispatch }) {
    commit(types.SET_CART_LOADED, false)

    await this.$graphql({
      requestPayload: {
        fetchPolicy: 'network-only',
        query: (await import('../../graphql/queries/customerCart.graphql')).default,
      },
      successHandler: async ({ data: { customerCart } }) => {
        const cartId = this.$cart.cartId()

        await dispatch('isCartReadOnly', customerCart.isReadonly)

        if (cartId !== customerCart.id && !customerCart.isReadonly) {
          await dispatch('mergeCarts', {
            sourceCartId: cartId,
            destinationCartId: customerCart.id,
          })
        } else {
          await dispatch('updateCart', customerCart)
        }
      },
    })

    commit(types.SET_CART_LOADED, true)
  },
  async getGuestCart ({ dispatch }) {
    await dispatch('getCartIdFromCookie')

    await dispatch('refreshCart')
  },
  reservationTimeout ({ commit, dispatch, state, getters }, reservationTimeout) {
    if (reservationTimeout && !state.reservationInterval) {
      const interval = setInterval(() => {
        commit(types.UPDATE_CART_RESERVATION_TIME)

        const SECOND_TO_SHOW_WARNING = 299

        if (getters.getSecondsDiff === SECOND_TO_SHOW_WARNING) {
          this.$toast.add({
            text: this.$i18n.t('Time to book your appointment is running out. Pay your order'),
            type: 'error',
            place: this.$toast.places.header,
            timeout: SECOND_TO_SHOW_WARNING * 1000,
          })
        }

        if (getters.getSecondsDiff === 0) {
          clearInterval(state.reservationInterval)
          commit(types.SET_CART_RESERVATION_INTERVAL, null)
          setTimeout(() => dispatch('refreshCartAfterTimeout'), 1000)
        }
      }, 1000)

      commit(types.UPDATE_CART_RESERVATION_TIME)
      commit(types.SET_CART_RESERVATION_INTERVAL, interval)
    }

    if (!reservationTimeout) {
      clearInterval(state.reservationInterval)
      commit(types.SET_CART_RESERVATION_INTERVAL, null)
    }
  },
  async refreshCartAfterTimeout ({ commit, dispatch, state }) {
    const prevItems = [...state.items]

    await dispatch('refreshCart')

    const currentItems = state.items.map(({ product }) => product.sku)
    const removedItems = prevItems.filter(({ product }) => !currentItems.includes(product.sku))

    commit(types.SET_CART_REMOVED_ITEMS, removedItems)

    if (removedItems.length) {
      this.$toast.add({
        component: () => import('../../components/CheckoutReservationTimeoutToast.vue'),
        props: {
          urlKey: removedItems[0]?.product.urlKey,
        },
        type: 'error',
        place: this.$toast.places.header,
        timeout: 5 * 60 * 1000,
      })
    }
  },
  clearRemovedItems ({ commit }) {
    commit(types.SET_CART_REMOVED_ITEMS, [])
  },
  updatePricesWithShipping ({ commit }, payload) {
    const {
      prices,
      mediclubPrices,
      nomediclubPrices,
    } = payload

    commit(types.SET_CART_PRICES, prices)
    commit(types.SET_CART_MEDICLUB_PRICES, mediclubPrices)
    commit(types.SET_CART_NO_MEDICLUB_PRICES, nomediclubPrices)
  },
  async updateCart ({ commit, dispatch, state, getters }, payload) {
    const { isRegisteredOnCheckout } = state

    const {
      items = [],
      prices,
      mediclubPrices,
      nomediclubPrices,
      id,
      appliedCoupons = [],
      appliedAmGiftCards = [],
      reservationTimeout = null,
      shippingAddresses = [],
      cartAttachments,
      availablePaymentMethods,
    } = payload

    commit(types.SET_CART_ITEMS, items)
    commit(types.SET_CART_PRICES, prices)
    commit(types.SET_CART_MEDICLUB_PRICES, mediclubPrices)
    commit(types.SET_CART_NO_MEDICLUB_PRICES, nomediclubPrices)
    commit(types.SET_CART_ID, id)
    commit(types.SET_APPLIED_COUPONS, appliedCoupons)
    commit(types.SET_APPLIED_GIFT_CARDS, appliedAmGiftCards)
    commit(types.SET_CART_LOADED, true)
    commit(types.SET_CART_RESERVATION_TIMEOUT, reservationTimeout)
    commit(types.SET_CART_ATTACHMENTS, cartAttachments)
    commit(types.SET_AVAILABLE_PAYMENT_METHODS, availablePaymentMethods)

    await dispatch('beneficiary/setQuoteFromCartItems', null, { root: true })

    if (!isRegisteredOnCheckout) {
      await dispatch('shipping/updateShipping', payload, { root: true })
    }

    await dispatch('shipping/setIsSameAsShippingSent', shippingAddresses, { root: true })

    if (process.client) {
      await dispatch('reservationTimeout', reservationTimeout)
    }

    if (items.length === 0) {
      commit(types.SET_CURRENT_STEP, 0)
    }
  },
  setCurrentStep ({ commit }, step) {
    commit(types.SET_CURRENT_STEP, step)
  },
  async removeItem ({ commit, dispatch }, itemId) {
    const cartId = this.$cart.cartId()

    commit(types.SET_CART_LOADED, false)

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/removeItemFromCart.graphql')).default,
        variables: {
          input: {
            cart_id: cartId,
            cart_item_uid: itemId,
          },
        },
      },
      successHandler: async ({ data: { removeItemFromCart } }) => {
        await dispatch('updateCart', removeItemFromCart.cart)
      },
    })

    commit(types.SET_CART_LOADED, true)
  },
  async removeItems ({ commit, dispatch }, itemsId) {
    const cartId = this.$cart.cartId()

    commit(types.SET_CART_LOADED, false)

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/removeItemsFromCart.graphql')).default,
        variables: {
          input: {
            cart_id: cartId,
            cart_items: itemsId,
          },
        },
      },
      successHandler: async ({ data: { removeItemsFromCart } }) => {
        await dispatch('updateCart', removeItemsFromCart.cart)
      },
    })

    commit(types.SET_CART_LOADED, true)
  },
  async removeAllItems ({ commit, dispatch }) {
    const cartId = this.$cart.cartId()

    commit(types.SET_CART_LOADED, false)

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/removeAllItemsFromCart.graphql')).default,
        variables: {
          input: {
            cart_id: cartId,
          },
        },
      },
      successHandler: async ({ data: { removeAllItemsFromCart } }) => {
        await dispatch('updateCart', removeAllItemsFromCart.cart)
      },
    })

    commit(types.SET_CART_LOADED, true)
  },
  async mergeCarts ({ commit, dispatch, getters }, { sourceCartId, destinationCartId }) {
    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/mergeCarts.graphql')).default,
        variables: {
          sourceCartId,
          destinationCartId,
        },
      },
      successHandler: async ({ data: { mergeCarts } }) => {
        await dispatch('updateCart', mergeCarts)

        await dispatch('shipping/setShippingForItems', null, { root: true })

        if (getters.getCurrentStep === 3) {
          const customer = this.$customer.getCustomer()

          await dispatch('shipping/setShippingAddressForItems', customer, { root: true })
        }
      },
    })
  },
  async getCart ({ dispatch }) {
    await dispatch('getCartIdFromCookie')

    await dispatch('refreshCart')
  },
  resetCart ({ commit }) {
    commit(types.RESET_CART)
  },
  setPaymentMethods ({ commit }, payload = []) {
    commit(types.PAYMENT_METHODS, payload)
  },
  setPaymentMethodId ({ commit }, payload) {
    commit(types.SET_PAYMENT_METHOD_ID, payload)
    this.$cookies.set('chosen-payment-method', payload, {
      path: '/',
    })
  },

  async updateCartItems ({ commit, dispatch, getters }, { uid, quantity, customizableOptions }) {
    const cartId = this.$cart.cartId()

    commit(types.SET_CART_LOADED, false)

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/updateCartItems.graphql')).default,
        variables: {
          input: {
            cart_id: cartId,
            cart_items: [
              {
                cart_item_uid: uid,
                quantity,
                customizable_options: customizableOptions,
              },
            ],
          },
        },
      },

      successHandler: async ({ data: { updateCartItems } }) => {
        await dispatch('updateCart', updateCartItems.cart)
        await dispatch('shipping/setShippingForItems', null, { root: true })

        if (getters.getCurrentStep === 3) {
          await dispatch('shipping/updateShipping', updateCartItems.cart, { root: true })
          await dispatch('shipping/setShippingAddressForItems', setAddressItem(updateCartItems.cart.shippingAddresses), { root: true })
        }
      },
    })

    commit(types.SET_CART_LOADED, true)
  },
  async updateMedicalPackageProducts ({ commit, dispatch }, { productsCart, grupingNumber }) {
    const cartId = this.$cart.cartId()

    commit(types.SET_CART_LOADED, false)

    await this.$graphql({
      requestPayload: {
        mutation: (await import('../../graphql/mutations/updateMedicalPackageProductsInCart.graphql')).default,
        variables: {
          input: {
            cart_id: cartId,
            grouping_number: grupingNumber,
            cart_items: camelCaseKeysToUnderscore(productsCart),
          },
        },
      },

      successHandler: async ({ data: { updateMedicalPackageProductsInCart } }) => {
        await dispatch('updateCart', updateMedicalPackageProductsInCart.cart)
      },
    })

    commit(types.SET_CART_LOADED, true)
  },
  setCartLoaded ({ commit }, payload) {
    commit(types.SET_CART_LOADED, payload)
  },

  isRegisteredOnCheckout ({ commit }, payload) {
    commit(types.IS_REGISTERED_ON_CHECKOUT, payload)
  },

  isCartReadOnly ({ commit }, payload) {
    commit(types.IS_CART_READ_ONLY, payload)
  },

  setShippingMethodReadOnly ({ commit }, payload) {
    commit(types.SET_SHIPPING_METHOD_READ_ONLY, payload)
  },

  setBillingAddress ({ commit }, payload) {
    commit(types.SET_BILLING_ADDRESS, payload)
  },
}
